import React, { useState } from "react";
import styled, { css } from "styled-components";
import {
  FiArrowLeft,
  FiTruck,
  FiUser,
  FiMapPin,
  FiDollarSign,
  FiPackage,
  FiFile,
  FiCopy,
  FiCheck,
  FiFileText,
  FiExternalLink,
} from "react-icons/fi";
import api from "../../services/api";
import { tokens, surface, hairline } from "../../styles/designTokens";

const CTE_COLOR = "#3b82f6";
const _mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const _eyebrow = css`
  ${_mono};
  font-size: 0.58em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

/* ═══════ STYLED COMPONENTS ═══════ */

const DetailContainer = styled.div`
  animation: detailFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;

  @keyframes detailFadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 24px 0 8px 0;
  margin-bottom: 1.5em;

  .back-btn {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    cursor: pointer;
    padding: 0;
    margin-top: 4px;
    flex-shrink: 0;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    svg { font-size: 1em; }
    &:hover {
      opacity: 1;
      background: ${({ theme }) => surface(theme, 1)};
      border-color: ${({ theme }) => hairline(theme, true)};
      transform: translateX(-1px);
    }
  }

  .header-details {
    flex: 1;
    min-width: 0;

    h3 {
      margin: 2px 0 8px 0;
      font-size: 1.4em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.02em;

      &::before {
        content: "MOVIMENTAÇÃO · CT-e";
        display: block;
        ${_eyebrow};
        opacity: 0.4;
        margin-bottom: 4px;
      }
    }

    .header-meta {
      display: flex;
      gap: 6px;
      align-items: center;
      flex-wrap: wrap;

      span {
        ${_mono};
        display: inline-flex;
        align-items: center;
        padding: 3px 9px;
        border-radius: ${tokens.radiusPill};
        font-size: 0.6em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        border: 1px solid;
      }

      .status-tag {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};
        &.cancelled {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239, 68, 68, 0.22);
        }
      }

      .type-tag {
        background: ${CTE_COLOR}14;
        color: ${CTE_COLOR};
        border-color: ${CTE_COLOR}38;
      }

      .date-tag {
        background: ${({ theme }) => surface(theme, 1)};
        color: ${({ theme }) => theme.text};
        border-color: ${({ theme }) => hairline(theme)};
        opacity: 0.7;
      }
    }
  }

  .header-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;

    button {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: transparent;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      opacity: 0.85;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;
      svg { font-size: 0.95em; }

      &:hover {
        opacity: 1;
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      &.btn-primary {
        background: ${tokens.brand};
        border-color: ${tokens.brand};
        color: #fff;
        opacity: 1;
        &:hover { background: ${tokens.brandHover}; border-color: ${tokens.brandHover}; transform: translateY(-1px); }
      }
    }
  }

  @media (max-width: 640px) {
    flex-wrap: wrap;
    .header-actions { width: 100%; justify-content: flex-end; }
    .header-actions button span { display: none; }
  }
`;

const HeroCard = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 1.5em;

  .hero-item {
    padding: 16px 18px;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: border-color ${tokens.durBase} ${tokens.easeOut};

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
    }

    .hero-label {
      ${_eyebrow};
      font-size: 0.55em;
      opacity: 0.45;
    }

    .hero-value {
      ${_mono};
      font-size: 0.95em;
      color: ${({ theme }) => theme.text};
      font-weight: 500;
      line-height: 1.3;
      letter-spacing: -0.01em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.highlight {
        font-size: 1.3em;
        font-weight: 600;
        color: ${CTE_COLOR};
        letter-spacing: -0.02em;
      }

      &.mono {
        font-size: 0.78em;
        font-weight: 500;
        opacity: 0.5;
        letter-spacing: 0.02em;
      }
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
`;

const TabsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 1.5em;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  button {
    padding: 11px 16px;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    ${_mono};
    font-size: 0.66em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover { opacity: 0.8; }

    &.active {
      opacity: 1;
      color: ${CTE_COLOR};
      border-bottom-color: ${CTE_COLOR};
    }

    svg { font-size: 1.2em; }
  }

  /* Botão DACTE: CTA verde flat com glow sutil, separado das abas */
  button.pdf-btn {
    margin-left: auto;
    margin-bottom: 6px;
    padding: 9px 18px;
    background: ${tokens.brand};
    border: 1px solid ${tokens.brand};
    border-bottom: 1px solid ${tokens.brand};
    border-radius: ${tokens.radiusMd};
    color: #fff;
    opacity: 1;
    font-family: ${tokens.fontSans};
    font-size: 0.82em;
    font-weight: 600;
    text-transform: none;
    letter-spacing: -0.005em;
    gap: 8px;
    box-shadow: 0 2px 10px ${tokens.brandGlow};

    svg { font-size: 1em; }

    .ext-icon {
      font-size: 0.85em;
      opacity: 0.75;
      transition: transform ${tokens.durBase} ${tokens.easeOut};
    }

    &:hover:not(:disabled) {
      opacity: 1;
      background: ${tokens.brandHover};
      border-color: ${tokens.brandHover};
      transform: translateY(-1px);
      box-shadow: 0 6px 18px ${tokens.brandGlow};

      .ext-icon { transform: translate(2px, -2px); opacity: 1; }
    }

    &:active:not(:disabled) { transform: translateY(0); }

    &:disabled {
      opacity: 0.6;
      cursor: wait;
      box-shadow: none;
    }
  }
`;

const DetailBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const SectionCard = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  margin-bottom: 20px;
  &:last-child { margin-bottom: 0; }
`;

const SectionTitle = styled.h4`
  ${_eyebrow};
  font-size: 0.62em;
  opacity: 0.45;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  svg { font-size: 1.1em; }
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  @media (max-width: 480px) { grid-template-columns: 1fr 1fr; gap: 8px; }
`;

const DataItem = styled.div`
  background: ${(props) => props.$highlight
    ? `${CTE_COLOR}10`
    : surface(props.theme, 1)};
  border: 1px solid ${(props) => props.$highlight
    ? `${CTE_COLOR}30`
    : hairline(props.theme)};
  border-radius: ${tokens.radiusMd};
  padding: 14px 16px;
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &:hover {
    border-color: ${(props) => props.$highlight
      ? `${CTE_COLOR}50`
      : hairline(props.theme, true)};
  }

  .label {
    ${_eyebrow};
    font-size: 0.55em;
    opacity: 0.45;
    margin-bottom: 6px;
  }

  .value {
    ${_mono};
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: -0.01em;
  }

  &.full-width { grid-column: 1 / -1; }
  &.span-2 { grid-column: span 2; }
`;

const StatusBadge = styled.span`
  ${_mono};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: ${tokens.radiusPill};
  font-size: 0.62em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: 1px solid;
  background: ${(props) =>
    props.$status === "Autorizada" ? tokens.brandSoft : tokens.dangerSoft};
  color: ${(props) =>
    props.$status === "Autorizada" ? tokens.brand : tokens.danger};
  border-color: ${(props) =>
    props.$status === "Autorizada" ? tokens.brandRing : "rgba(239, 68, 68, 0.22)"};

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const PdfContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 550px;
  border-radius: ${tokens.radiusMd};
  overflow: hidden;
  border: 1px solid ${({ theme }) => hairline(theme)};
  @media (max-width: 480px) { min-height: 400px; }
`;

const PdfToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => surface(theme, 1)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  .toolbar-label {
    ${_eyebrow};
    font-size: 0.6em;
    opacity: 0.5;
    display: flex;
    align-items: center;
    gap: 8px;
    svg { font-size: 1.1em; }
  }

  button {
    padding: 6px 12px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusSm};
    color: ${({ theme }) => theme.text};
    font-family: ${tokens.fontSans};
    font-size: 0.72em;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    &:hover { background: ${({ theme }) => surface(theme, 1)}; border-color: ${({ theme }) => hairline(theme, true)}; }
    svg { font-size: 0.95em; }
  }
`;

const PdfViewer = styled.iframe`
  width: 100%;
  flex: 1;
  border: none;
  background: #525659;
  min-height: 500px;
  @media (max-width: 480px) { min-height: 360px; }
`;

const LoadingPdf = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: ${({ theme }) => theme.text};
  gap: 12px;

  .spinner {
    width: 28px;
    height: 28px;
    border: 2.5px solid ${({ theme }) => hairline(theme)};
    border-top-color: ${CTE_COLOR};
    border-radius: 50%;
    animation: detailSpin 0.8s linear infinite;
  }

  @keyframes detailSpin { to { transform: rotate(360deg); } }

  .loading-text {
    font-size: 13px;
    opacity: 0.4;
    font-weight: 500;
  }
`;

/* ═══════ COMPONENT ═══════ */

const CTeDetailView = ({ doc, onBack }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  if (!doc) return null;

  const cte = doc;

  const formatCurrency = (value) => {
    if (!value) return "R$ 0,00";
    return `R$ ${parseFloat(value).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("pt-BR");
  };

  const parseCarga = (carga) => {
    if (!carga) return null;
    if (typeof carga === "string") {
      try { return JSON.parse(carga); } catch { return null; }
    }
    return carga;
  };

  const openDacte = async () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
      return;
    }
    setLoadingPdf(true);
    try {
      const response = await api.get(`/movimentacoes/dacte/${cte.chave}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      setPdfUrl(url);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Erro ao carregar DACTE:", error);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleCopyKey = () => {
    if (cte.chave) {
      navigator.clipboard.writeText(cte.chave);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const cargaData = parseCarga(cte.carga);
  const totalPrest = formatCurrency(cte.total?.vTPrest || cte.valor);

  return (
    <DetailContainer>
      {/* Header */}
      <HeaderBar>
        <button className="back-btn" onClick={onBack} title="Voltar">
          <FiArrowLeft />
        </button>
        <div className="header-details">
          <h3>Conhecimento de Transporte</h3>
          <div className="header-meta">
            <span className={`status-tag ${cte.status !== "Autorizada" ? "cancelled" : ""}`}>
              {cte.status || "N/A"}
            </span>
            <span className="type-tag">CTe {cte.num || cte.numero || "—"}</span>
            <span className="date-tag">Série {cte.serie || "—"}</span>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={handleCopyKey} title="Copiar chave de acesso">
            {copiedKey ? <FiCheck /> : <FiCopy />}
            <span>{copiedKey ? "Copiado" : "Copiar chave"}</span>
          </button>
          <button className="btn-primary" onClick={openDacte}>
            <FiFile />
            <span>{loadingPdf ? "Abrindo..." : "DACTE"}</span>
          </button>
        </div>
      </HeaderBar>

      {/* Hero */}
      <HeroCard>
        <div className="hero-item">
          <div className="hero-label">Valor da Prestação</div>
          <div className="hero-value highlight">{totalPrest}</div>
        </div>
        <div className="hero-item">
          <div className="hero-label">Transportadora</div>
          <div className="hero-value">{cte.emiNome || cte.emitente || "N/A"}</div>
        </div>
        <div className="hero-item">
          <div className="hero-label">Data Emissão</div>
          <div className="hero-value">{formatDate(cte.dhEvento || cte.data)}</div>
        </div>
        <div className="hero-item">
          <div className="hero-label">Chave de Acesso</div>
          <div className="hero-value mono">{cte.chave || "N/A"}</div>
        </div>
      </HeroCard>

      {/* Tabs */}
      <TabsBar>
        <button className="active">
          <FiTruck /> Informações
        </button>
        <button className="pdf-btn" onClick={openDacte} disabled={loadingPdf}>
          <FiFileText />
          {loadingPdf ? "Abrindo..." : "Abrir DACTE"}
          {!loadingPdf && <FiExternalLink className="ext-icon" />}
        </button>
      </TabsBar>

      {/* Body */}
      <DetailBody>
            {/* Informações Gerais */}
            <SectionCard>
              <SectionTitle><FiTruck /> Informações Gerais</SectionTitle>
              <DataGrid>
                <DataItem>
                  <div className="label">Número</div>
                  <div className="value">{cte.num || cte.numero || "N/A"}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Série</div>
                  <div className="value">{cte.serie || "N/A"}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Data/Hora Emissão</div>
                  <div className="value">{formatDate(cte.dhEvento || cte.data)}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Status</div>
                  <div className="value">
                    <StatusBadge $status={cte.status}>{cte.status || "N/A"}</StatusBadge>
                  </div>
                </DataItem>
                <DataItem>
                  <div className="label">Natureza da Operação</div>
                  <div className="value">{cte.natOp || "N/A"}</div>
                </DataItem>
                <DataItem>
                  <div className="label">CFOP</div>
                  <div className="value">{cte.cfop || "N/A"}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Manifestação</div>
                  <div className="value">{cte.manifest || "Sem Manifestação"}</div>
                </DataItem>
                <DataItem className="full-width">
                  <div className="label">Chave de Acesso</div>
                  <div className="value" style={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: 13, opacity: 0.6, wordBreak: "break-all", letterSpacing: "0.5px" }}>
                    {cte.chave || "N/A"}
                  </div>
                </DataItem>
              </DataGrid>
            </SectionCard>

            {/* Transportadora + Tomador side by side */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginBottom: 16 }}>
              <SectionCard style={{ marginBottom: 0 }}>
                <SectionTitle><FiUser /> Transportadora (Emitente)</SectionTitle>
                <DataGrid>
                  <DataItem className="span-2">
                    <div className="label">Nome/Razão Social</div>
                    <div className="value">{cte.emiNome || cte.emitente || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">CNPJ</div>
                    <div className="value">{cte.emiDoc || cte.cnpj || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Cidade</div>
                    <div className="value">{cte.emiCidade || "N/A"}</div>
                  </DataItem>
                </DataGrid>
              </SectionCard>

              <SectionCard style={{ marginBottom: 0 }}>
                <SectionTitle><FiMapPin /> Tomador do Serviço</SectionTitle>
                <DataGrid>
                  <DataItem className="span-2">
                    <div className="label">Nome/Razão Social</div>
                    <div className="value">{cte.destNome || cte.destinatario || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">CNPJ/CPF</div>
                    <div className="value">{cte.destDoc || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Cidade</div>
                    <div className="value">{cte.destCidade || "N/A"}</div>
                  </DataItem>
                </DataGrid>
              </SectionCard>
            </div>

            {/* Valores do Serviço */}
            <SectionCard>
              <SectionTitle><FiDollarSign /> Valores do Serviço</SectionTitle>
              <DataGrid>
                <DataItem $highlight>
                  <div className="label">Valor da Prestação</div>
                  <div className="value" style={{ fontWeight: 800, fontSize: 16, color: "#3b82f6" }}>{totalPrest}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Valor a Receber</div>
                  <div className="value">{formatCurrency(cte.total?.vRec)}</div>
                </DataItem>
                {cte.total?.Comp && (
                  <>
                    <DataItem>
                      <div className="label">Componente</div>
                      <div className="value">{cte.total.Comp.xNome || "N/A"}</div>
                    </DataItem>
                    <DataItem>
                      <div className="label">Valor do Componente</div>
                      <div className="value">{formatCurrency(cte.total.Comp.vComp)}</div>
                    </DataItem>
                  </>
                )}
              </DataGrid>
            </SectionCard>

            {/* Informações da Carga */}
            {cargaData && (
              <SectionCard>
                <SectionTitle><FiPackage /> Informações da Carga</SectionTitle>
                <DataGrid>
                  <DataItem>
                    <div className="label">Valor da Carga</div>
                    <div className="value">{formatCurrency(cargaData.vCarga)}</div>
                  </DataItem>
                  <DataItem className="span-2">
                    <div className="label">Produto Predominante</div>
                    <div className="value">{cargaData.proPred || "N/A"}</div>
                  </DataItem>
                  <DataItem className="span-2">
                    <div className="label">Outras Características</div>
                    <div className="value">{cargaData.xOutCat || "N/A"}</div>
                  </DataItem>
                  {cargaData.infQ && (
                    <>
                      <DataItem>
                        <div className="label">Unidade de Medida</div>
                        <div className="value">{cargaData.infQ.tpMed || "N/A"}</div>
                      </DataItem>
                      <DataItem>
                        <div className="label">Quantidade</div>
                        <div className="value">{cargaData.infQ.qCarga || "N/A"}</div>
                      </DataItem>
                    </>
                  )}
                </DataGrid>
              </SectionCard>
            )}
      </DetailBody>
    </DetailContainer>
  );
};

export default CTeDetailView;
